import {
  CaretLeftIcon,
  CaretRightIcon,
  CrownSimpleIcon,
  FunnelIcon,
  UserPlusIcon,
} from "@phosphor-icons/react";
import { cn } from "@reactive-resume/utils";
import { Link } from "react-router";

import { USER_SPACE_PATHS } from "../../../config/navigation";
import {
  REFERRAL_PAGE_SIZE,
  type ReferralListItem,
  type ReferralStatusFilter,
} from "../types/referral";

type ReferralsListProps = {
  referrals: ReferralListItem[];
  activeCount: number;
  inactiveCount: number;
  filter: ReferralStatusFilter;
  page: number;
  onFilterChange: (filter: ReferralStatusFilter) => void;
  onPageChange: (page: number) => void;
};

const formatDate = (value: string) => {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "Date inconnue";
  }

  return new Intl.DateTimeFormat("fr-FR", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(date);
};

const ReferralStatusBadge = ({ status }: { status: ReferralListItem["status"] }) => {
  const isActive = status === "active";

  return (
    <span
      className={cn(
        "inline-flex rounded-full px-2.5 py-1",
        "text-[10px] font-semibold",
        isActive ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600",
      )}
    >
      {isActive ? "Actif" : "Inactif"}
    </span>
  );
};

export const ReferralsList = ({
  referrals,
  activeCount,
  inactiveCount,
  filter,
  page,
  onFilterChange,
  onPageChange,
}: ReferralsListProps) => {
  const pageCount = Math.max(1, Math.ceil(referrals.length / REFERRAL_PAGE_SIZE));

  const safePage = Math.min(page, pageCount);

  const firstIndex = (safePage - 1) * REFERRAL_PAGE_SIZE;

  const visibleReferrals = referrals.slice(firstIndex, firstIndex + REFERRAL_PAGE_SIZE);

  const firstVisible = referrals.length === 0 ? 0 : firstIndex + 1;

  const lastVisible = Math.min(firstIndex + REFERRAL_PAGE_SIZE, referrals.length);

  return (
    <article className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <header className="flex flex-col gap-3 border-b border-slate-200 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-sm font-bold text-indigo-950 sm:text-base">Mes filleuls</h2>

            <span className="text-[11px] text-slate-500">
              {activeCount} actif
              {activeCount > 1 ? "s" : ""}
              {" · "}
              {inactiveCount} inactif
              {inactiveCount > 1 ? "s" : ""}
            </span>
          </div>

          <p className="mt-1 text-[11px] text-slate-600 sm:text-xs">
            Suivez l’inscription et la qualification des personnes invitées.
          </p>
        </div>

        <label className="relative block w-full sm:w-auto">
          <span className="sr-only">Filtrer les filleuls</span>

          <FunnelIcon
            aria-hidden
            size={16}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-indigo-700"
          />

          <select
            value={filter}
            onChange={(event) => {
              onFilterChange(event.target.value as ReferralStatusFilter);
            }}
            className={cn(
              "h-10 w-full appearance-none rounded-xl",
              "border border-slate-200 bg-white",
              "pl-9 pr-9 text-xs font-semibold text-indigo-950",
              "outline-none transition-colors",
              "focus:border-indigo-500 focus:ring-2",
              "focus:ring-indigo-100 sm:min-w-44",
            )}
          >
            <option value="all">Tous les statuts</option>

            <option value="active">Actifs</option>

            <option value="inactive">Inactifs</option>
          </select>
        </label>
      </header>

      {visibleReferrals.length === 0 ? (
        <div className="flex min-h-[240px] flex-col items-center justify-center px-5 py-8 text-center sm:min-h-[270px]">
          <span className="grid size-16 place-items-center rounded-full bg-gradient-to-br from-indigo-50 to-violet-100 text-indigo-600">
            <UserPlusIcon aria-hidden size={31} weight="duotone" />
          </span>

          <h3 className="mt-4 text-base font-bold text-indigo-950">Aucun filleul</h3>

          <p className="mt-1.5 max-w-md text-xs leading-5 text-slate-600">
            Vos filleuls apparaîtront ici après l’utilisation de votre lien de parrainage.
          </p>

          <Link
            to={USER_SPACE_PATHS.offers}
            className={cn(
              "mt-4 flex min-h-10 items-center gap-2",
              "rounded-xl bg-indigo-700 px-4",
              "text-xs font-semibold text-white",
              "transition-colors hover:bg-indigo-800",
              "focus-visible:outline-none",
              "focus-visible:ring-2",
              "focus-visible:ring-indigo-500",
            )}
          >
            <CrownSimpleIcon aria-hidden size={17} weight="fill" className="text-amber-300" />
            Découvrir les offres
          </Link>
        </div>
      ) : (
        <>
          <div className="divide-y divide-slate-100 md:hidden">
            {visibleReferrals.map((referral) => (
              <div key={referral.id} className="p-4">
                <div className="flex items-start gap-3">
                  <span className="grid size-9 shrink-0 place-items-center rounded-full bg-indigo-50 text-[11px] font-bold text-indigo-700">
                    {referral.initials}
                  </span>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="truncate text-xs font-bold text-indigo-950">
                          {referral.name}
                        </p>

                        <p className="mt-0.5 truncate text-[10px] text-slate-500">
                          {referral.email}
                        </p>
                      </div>

                      <ReferralStatusBadge status={referral.status} />
                    </div>

                    <div className="mt-3 grid grid-cols-2 gap-2 text-[10px]">
                      <div>
                        <p className="text-slate-400">Inscription</p>

                        <p className="mt-0.5 font-medium text-slate-700">
                          {formatDate(referral.registeredAt)}
                        </p>
                      </div>

                      <div>
                        <p className="text-slate-400">Offre</p>

                        <p className="mt-0.5 font-medium text-slate-700">
                          {referral.offerName ?? "Aucune"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="hidden overflow-x-auto md:block">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-slate-200 text-left">
                  <th className="px-5 py-3 text-[11px] font-semibold text-slate-500">Filleul</th>

                  <th className="px-5 py-3 text-[11px] font-semibold text-slate-500">
                    Date d’inscription
                  </th>

                  <th className="px-5 py-3 text-[11px] font-semibold text-slate-500">Offre</th>

                  <th className="px-5 py-3 text-[11px] font-semibold text-slate-500">Statut</th>
                </tr>
              </thead>

              <tbody>
                {visibleReferrals.map((referral) => (
                  <tr key={referral.id} className="border-b border-slate-100 last:border-b-0">
                    <td className="px-5 py-2.5">
                      <div className="flex items-center gap-2.5">
                        <span className="grid size-8 shrink-0 place-items-center rounded-full bg-indigo-50 text-[10px] font-bold text-indigo-700">
                          {referral.initials}
                        </span>

                        <div className="min-w-0">
                          <p className="truncate text-xs font-semibold text-indigo-950">
                            {referral.name}
                          </p>

                          <p className="truncate text-[10px] text-slate-400">{referral.email}</p>
                        </div>
                      </div>
                    </td>

                    <td className="px-5 py-2.5 text-xs text-slate-600">
                      {formatDate(referral.registeredAt)}
                    </td>

                    <td className="px-5 py-2.5 text-xs text-slate-600">
                      {referral.offerName ?? "Aucune"}
                    </td>

                    <td className="px-5 py-2.5">
                      <ReferralStatusBadge status={referral.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <footer className="flex min-h-12 items-center justify-between gap-3 border-t border-slate-200 px-4 sm:px-5">
        <p className="text-[10px] leading-4 text-slate-500 sm:text-[11px]">
          Le statut devient actif lorsqu’un filleul souscrit une offre éligible.
        </p>

        {referrals.length > 0 && (
          <div className="flex shrink-0 items-center gap-2">
            <span className="hidden text-[11px] text-slate-600 sm:inline">
              {firstVisible}–{lastVisible} sur {referrals.length}
            </span>

            <button
              type="button"
              disabled={safePage <= 1}
              onClick={() => {
                onPageChange(safePage - 1);
              }}
              aria-label="Page précédente"
              className="grid size-8 place-items-center rounded-lg border border-slate-200 text-indigo-700 disabled:cursor-not-allowed disabled:opacity-35"
            >
              <CaretLeftIcon aria-hidden size={15} weight="bold" />
            </button>

            <button
              type="button"
              disabled={safePage >= pageCount}
              onClick={() => {
                onPageChange(safePage + 1);
              }}
              aria-label="Page suivante"
              className="grid size-8 place-items-center rounded-lg border border-slate-200 text-indigo-700 disabled:cursor-not-allowed disabled:opacity-35"
            >
              <CaretRightIcon aria-hidden size={15} weight="bold" />
            </button>
          </div>
        )}
      </footer>
    </article>
  );
};
